// ============================================
// ForgotPasswordPage.js — Unified Dual Theme Architecture
// Fully Responsive with Hidden Scrollbars
// ============================================

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';

const ForgotPasswordPage = () => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle'); // idle, loading, success, error
    const [message, setMessage] = useState('');
    const { theme, toggleTheme } = useTheme();
    const location = useLocation();

    // Detect which portal this request is from
    const queryParams = new URLSearchParams(location.search);
    const role = queryParams.get('role') || 'driver'; // default driver
    const isPA = role === 'pa';
    // const portalLabel = isPA ? 'PA (Passenger Assistant) Portal' : 'Driver Portal';
    const portalShort = isPA ? 'PA Portal' : 'Driver Portal';

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('loading');
        try {
            const { data } = await api.post('/auth/forgot-password', { email, role });
            setStatus('success');
            setMessage(data.message || 'Password reset link sent to your email.');
        } catch (error) {
            setStatus('error');
            setMessage(error.response?.data?.message || 'Failed to send reset email.');
        }
    };

    return (
        <div className="h-screen w-screen flex bg-slate-50 dark:bg-[#050510] overflow-hidden font-['Inter',sans-serif] transition-colors duration-500">
            {/* ── Left Panel: Cinematic Image Overlay ───────── */}
            <div className="hidden lg:flex lg:w-[45%] xl:w-[50%] relative group overflow-hidden">
                <div className="absolute inset-0 bg-slate-900 dark:bg-black transition-colors duration-500">
                    <img
                        src="/assets/login-bg.png"
                        alt="IdaCab Premium Transport"
                        className="w-full h-full object-cover opacity-[0.85] dark:opacity-40 grayscale-[10%] group-hover:scale-105 transition-transform duration-[20s] ease-out mix-blend-overlay"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent transition-colors duration-500" />
                </div>

                <div className="relative z-10 flex flex-col justify-between p-16 w-full h-full border-r border-slate-200 dark:border-white/5 transition-colors">
                    <div className="flex justify-between items-start">
                        <div className="bg-white/95 p-1.5 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] inline-flex items-center justify-center backdrop-blur-sm">
                            <img src="/assets/logo.png" alt="IdaCab Logo" className="h-16 sm:h-24 w-auto object-contain" />
                        </div>
                        <div className="px-4 py-1.5 rounded-full bg-white/10 dark:bg-white/5 backdrop-blur-md border border-white/20 dark:border-white/10 text-white dark:text-white/90 text-[10px] font-black tracking-widest uppercase shadow-xl transition-all">
                            Account Recovery
                        </div>
                    </div>

                    <div className="max-w-lg mb-12 transform group-hover:translate-x-2 transition-transform duration-700 ease-out">
                        <h1 className="text-5xl lg:text-6xl font-black text-white leading-[1.1] mb-6 tracking-tight drop-shadow-2xl">
                            Reset Your
                            <span className="block text-teal-400">Access</span>
                        </h1>
                        <p className="text-white/90 text-lg leading-relaxed font-medium drop-shadow-md">
                            Enter your email to receive a secure password reset link and regain access to your IdaCab account.
                        </p>
                    </div>

                    <div className="flex items-center gap-6 text-white/50 text-[11px] font-black tracking-widest uppercase">
                        <span className="flex items-center gap-2">Secure</span>
                        <div className="w-1.5 h-1.5 rounded-full bg-white/30" />
                        <span className="flex items-center gap-2">Fast</span>
                    </div>
                </div>
            </div>

            {/* ── Right Panel: Form (Scrollbar Hidden) ────────── */}
            <div className="flex-1 h-full relative selection:bg-teal-500/20 z-10 flex flex-col justify-center items-center p-4 sm:p-8 lg:p-20 overflow-hidden">
                
                {/* Global Theme Toggle */}
                <div className="absolute top-6 right-6 z-50">
                    <button
                        onClick={toggleTheme}
                        className="p-3 rounded-full bg-white/50 dark:bg-slate-800/50 backdrop-blur-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-all border border-slate-200 dark:border-slate-700 hover:scale-105 active:scale-95 shadow-sm"
                        aria-label="Toggle theme"
                    >
                        {theme === 'light' ? (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        )}
                    </button>
                </div>

                {/* Main Glass Form Container */}
                <div className="w-full max-w-[460px] max-h-[100vh] sm:max-h-[95vh] overflow-y-auto scrollbar-hide bg-white dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] p-6 sm:p-8 shadow-2xl shadow-slate-200 dark:shadow-black/50 border border-slate-100 dark:border-white/5 transition-colors duration-500 relative">
                    
                    <div className="mb-6 flex flex-col items-center lg:hidden">
                        <div className="bg-white/95 p-1.5 rounded-2xl shadow-lg inline-flex items-center justify-center">
                            <img src="/assets/logo.png" alt="IdaCab Logo" className="h-16 w-auto object-contain" />
                        </div>
                    </div>

                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-black text-slate-800 dark:text-white tracking-tight mb-2 transition-colors">Forgot Password</h2>
                        <p className="text-slate-500 dark:text-slate-400 text-sm font-bold transition-colors">Recover your account</p>
                        {/* Portal Badge */}
                        <div className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-50 dark:bg-teal-500/10 border border-teal-200 dark:border-teal-500/30">
                            <div className={`w-2 h-2 rounded-full ${isPA ? 'bg-purple-500' : 'bg-teal-500'}`} />
                            <p className="text-teal-700 dark:text-teal-400 text-[10px] font-black uppercase tracking-widest">{portalShort}</p>
                        </div>
                    </div>

                    <div className="w-full">
                        {status === 'error' && (
                            <div className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/30 flex items-center gap-3 transition-colors">
                                <div className="w-2 h-2 rounded-full bg-red-500 dark:bg-red-400 animate-pulse shadow-sm" />
                                <p className="text-red-700 dark:text-red-400 text-xs font-bold uppercase tracking-wide leading-relaxed">{message}</p>
                            </div>
                        )}

                        {status === 'success' ? (
                            <div className="mb-8 p-6 rounded-2xl bg-teal-50 dark:bg-teal-500/10 border border-teal-100 dark:border-teal-500/30 text-center space-y-4 transition-colors">
                                <div className="mx-auto w-12 h-12 rounded-full bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center">
                                    <svg className="w-6 h-6 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                                </div>
                                <h3 className="text-teal-800 dark:text-teal-300 font-black text-lg">Check Your Email</h3>
                                <p className="text-teal-700 dark:text-teal-400 text-xs font-medium">{message}</p>
                                <div className="pt-4">
                                    <Link to="/login" className="inline-block px-6 py-3 rounded-xl bg-teal-600 hover:bg-teal-700 text-white text-xs font-black uppercase tracking-widest transition-colors shadow-lg shadow-teal-600/30">
                                        Back to Login
                                    </Link>
                                </div>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6 relative z-10">
                                <div className="space-y-1.5 capitalize tracking-wide">
                                    <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.15em] ml-1">Email Address</label>
                                    <input
                                        type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                                        className="w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:border-teal-500 focus:bg-white dark:focus:bg-slate-800 focus:ring-4 focus:ring-teal-500/10 outline-none transition-all text-sm font-medium shadow-sm hover:border-slate-200"
                                        placeholder="name@email.com"
                                    />
                                </div>

                                <button type="submit" disabled={status === 'loading'}
                                    className="w-full py-4 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white text-[11px] font-black uppercase tracking-[0.2em] shadow-lg shadow-teal-500/20 active:scale-[0.98] disabled:opacity-50 disabled:grayscale transition-all flex justify-center items-center h-[52px] relative overflow-hidden mt-4"
                                >
                                    {status === 'loading' ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <span className="relative z-10">Send Reset Link</span>}
                                </button>
                            </form>
                        )}

                        <div className="mt-8 text-center pt-2">
                            <p className="text-slate-500 dark:text-slate-400 text-[11px] font-bold tracking-wide uppercase transition-colors">
                                Remembered your password? <Link to="/login" className="text-teal-600 dark:text-teal-400 font-black hover:text-teal-700 dark:hover:text-teal-300 ml-1 underline-offset-4 hover:underline transition-colors">Log in</Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForgotPasswordPage;
